/**
 * Display helpers shared by every CRM screen.
 *
 * Locale is left to the browser (`undefined`) rather than hardcoded — the ERP
 * serves one organisation, but its people may not all be in one place.
 */

import type { Lead } from '@erp/contracts'
import type { Contact } from '@erp/contracts'

export function displayName(lead: Lead): string {
  const name = [lead.firstName, lead.lastName].filter(Boolean).join(' ')
  return name.length > 0 ? name : (lead.organizationName ?? lead.email ?? 'Untitled lead')
}

export function contactName(contact: Contact): string {
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ')
  return name.length > 0 ? name : (contact.email ?? 'Unnamed contact')
}

export function formatDate(iso: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(iso))
}

export function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  )
}

export function formatMoney(amount: number, currency: string | null = null): string {
  return new Intl.NumberFormat(undefined, {
    style: currency ? 'currency' : 'decimal',
    currency: currency ?? undefined,
    maximumFractionDigits: 0,
  }).format(amount)
}

/**
 * `<input type="datetime-local">` speaks local wall-clock with no zone, while
 * the contracts speak ISO instants. These two convert between them.
 */
export function toLocalInput(iso: string): string {
  const date = new Date(iso)
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
}

export function fromLocalInput(local: string): string {
  return new Date(local).toISOString()
}

export function isOverdue(iso: string | null): boolean {
  return iso !== null && new Date(iso).getTime() < Date.now()
}
